import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './admin/useAdminApi';

/**
 * Self-serve customer Settings hooks. These call the customer-scoped Netlify
 * functions (customer-get-settings / customer-update-settings /
 * customer-invite-user / customer-remove-user), which pin all scope to the
 * caller's active customer server-side. No customer_id is ever sent — the
 * backend resolves it from the caller's session.
 *
 * adminFetch is reused only as a thin authed-fetch helper (injects the JWT,
 * unwraps `{ ok, ... }`). It is NOT an admin-only gate; the customer-*
 * functions enforce their own customer-admin / member checks.
 */

export type SelfServeRole = 'member' | 'admin';

/** Routing roles a recipient field can carry. */
export type BillingRole = 'billing' | 'accounting' | 'remittance' | 'optional';

export const BILLING_ROLES: readonly BillingRole[] = ['billing', 'accounting', 'remittance'];

export interface TeamMember {
  id: string;
  full_name: string | null;
  email: string | null;
  role: string;
  active: boolean;
  last_sign_in_at: string | null;
  created_at: string;
}

export interface CustomerCommSettings {
  billing_email: string | null;
  accounting_email: string | null;
  remittance_email: string | null;
  optional_email: string | null;
  reminders_enabled: boolean;
  statements_enabled: boolean;
  invoice_recipients: string[];
  statement_recipients: string[];
  reminder_recipients: string[];
  order_update_recipients: string[];
  paper_statements_enabled: boolean;
  paper_invoices_enabled: boolean;
}

export interface CustomerSettingsResponse {
  ok: true;
  customer_id: string;
  customer_name: string;
  caller_role: string | null;
  is_admin: boolean;
  /** admin/owner/ap — may edit billing contacts + routing (not the team). */
  can_edit_billing?: boolean;
  is_superadmin: boolean;
  settings: CustomerCommSettings;
  team: TeamMember[];
}

const SETTINGS_KEY = ['customer-settings'] as const;

export function useCustomerSettings() {
  return useQuery<CustomerSettingsResponse>({
    queryKey: SETTINGS_KEY,
    queryFn: () => adminFetch<CustomerSettingsResponse>('customer-get-settings'),
    staleTime: 30 * 1000,
  });
}

export type UpdateSettingsInput = Partial<{
  billing_email: string | null;
  accounting_email: string | null;
  remittance_email: string | null;
  optional_email: string | null;
  reminders_enabled: boolean;
  statements_enabled: boolean;
  invoice_recipients: string[];
  statement_recipients: string[];
  reminder_recipients: string[];
  order_update_recipients: string[];
  paper_statements_enabled: boolean;
  paper_invoices_enabled: boolean;
}>;

export function useUpdateCustomerSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateSettingsInput) =>
      adminFetch<{ ok: true; customer_id: string }>('customer-update-settings', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });
}

export interface InviteTeammateInput {
  email: string;
  full_name: string;
  phone?: string;
  role: SelfServeRole;
  send_welcome?: boolean;
}

export function useInviteTeammate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteTeammateInput) =>
      adminFetch<{
        ok: true;
        user_id: string;
        email: string;
        customer_id: string;
        role: SelfServeRole;
        membership_action: 'created' | 'reactivated' | 'updated' | 'already_active';
        new_auth_user: boolean;
        email_sent: boolean;
        email_warning: string | null;
      }>('customer-invite-user', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });
}

export function useRemoveTeammate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { user_id: string }) =>
      adminFetch<{ ok: true; user_id: string; customer_id: string }>('customer-remove-user', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });
}
